import { useEffect, useState } from "react";

export type LatLngLiteral = {
  lat: number;
  lng: number;
};

export type PlaceIdLookupStatus =
  | "idle"
  | "loading"
  | "found"
  | "invalid"
  | "notFound"
  | "quotaExceeded"
  | "denied"
  | "error";

export interface IPlaceIdLookup {
  status: PlaceIdLookupStatus;
  coords: LatLngLiteral | null;
}

// Real Google Place IDs are 27+ characters — anything shorter is still
// being typed, so looking it up would only burn quota on partial input.
const MIN_PLACE_ID_LENGTH = 20;

// Maps a failed Places API (New) response to a specific status. Places New
// returns 400/INVALID_ARGUMENT for a malformed or non-existent Place ID,
// 429/RESOURCE_EXHAUSTED for quota, 403/PERMISSION_DENIED for a blocked
// key/API; anything else is a generic failure.
const classifyError = (
  httpStatus: number,
  reason: string
): PlaceIdLookupStatus => {
  if (httpStatus === 400 || reason === "INVALID_ARGUMENT") return "invalid";
  if (httpStatus === 404 || reason === "NOT_FOUND") return "notFound";
  if (httpStatus === 429 || reason === "RESOURCE_EXHAUSTED") return "quotaExceeded";
  if (httpStatus === 403 || reason === "PERMISSION_DENIED") return "denied";
  return "error";
};

// Debounced Place ID lookup against the Places API (New), using the single
// runtime-delivered browser key (v2/Config/MapsKey). Only sessions the
// backend trusts with that key can resolve Place IDs at all.
const usePlaceIdLookup = (
  placeId?: string,
  apiKey?: string | null,
  debounceMs = 600
): IPlaceIdLookup => {
  const [lookup, setLookup] = useState<IPlaceIdLookup>({
    status: "idle",
    coords: null
  });

  useEffect(() => {
    const trimmed = placeId?.trim();
    if (!trimmed || trimmed.length < MIN_PLACE_ID_LENGTH || !apiKey) {
      setLookup({ status: "idle", coords: null });
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(() => {
      setLookup({ status: "loading", coords: null });
      void fetch(
        `https://places.googleapis.com/v1/places/${encodeURIComponent(
          trimmed
        )}?fields=location&key=${apiKey}`
      )
        .then(async (response) => {
          if (cancelled) return;
          const body = await response.json().catch(() => null);

          if (response.ok) {
            if (body?.location) {
              setLookup({
                status: "found",
                coords: {
                  lat: body.location.latitude,
                  lng: body.location.longitude
                }
              });
            } else {
              setLookup({ status: "notFound", coords: null });
            }
            return;
          }

          const reason: string = body?.error?.status ?? "";
          setLookup({ status: classifyError(response.status, reason), coords: null });
        })
        .catch(() => {
          if (!cancelled) setLookup({ status: "error", coords: null });
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [placeId, apiKey, debounceMs]);

  return lookup;
};

export default usePlaceIdLookup;
